import { DocContent } from '../docsData';

const doc: DocContent = {
  title: 'Datadog',
  content: `
A Datadog connection does two things:

- **Starts agents on alerts.** A Datadog webhook sends each monitor alert to Pliable, and an agent with a trigger on the connection runs on it. Repeats of one monitor join the same piece of work.
- **Lets agents read Datadog.** Logs, monitors, events, incidents, traces and metrics, read-only, with your keys.

---

## 1. Create an API key and an application key

The agent reads Datadog as whoever creates these keys, so that person needs at least the permissions below.

1. In Datadog, open **Organization Settings → API Keys** and click **New Key**. Name it (for example \`pliable-agents\`) and copy it.
2. Open **Organization Settings → Application Keys** and click **New Key**. Under **Scopes**, choose these, and no \`_write\` scope:

| Scope | For |
|---|---|
| \`mcp_read\` | Datadog's MCP server, which agents read through |
| \`logs_read_data\`, \`logs_read_index_data\` | searching and analysing logs |
| \`monitors_read\` | monitors |
| \`events_read\` | events |
| \`incident_read\` | incidents |
| \`apm_read\` | traces (spans) |
| \`metrics_read\`, \`timeseries_query\` | metrics and their values |

---

## 2. Add the connection in Pliable

1. Open the **Agents** page and find **Connections**.
2. Choose **Datadog**, give the connection a name (for example \`ops-datadog\`), and paste both keys.
3. Pliable fills in a **webhook secret** for you. Copy it before you click **Add connection**: Datadog needs it in step 3, and Pliable never shows it again.

To change a key later, use **Replace secret**. It fills in a **new** webhook secret: paste the current one over it, or put the new one in Datadog's webhook, or alerts stop arriving.

The new connection shows a **delivery URL**. Copy it for the next step.

---

## 3. Send alerts to Pliable

1. In Datadog, open **Integrations → Webhooks** and click **New**.
2. **Name:** something short, for example \`pliable\`. Monitors mention it as \`@webhook-pliable\`.
3. **URL:** the connection's delivery URL.
4. **Auth method:** **None**.
5. **Custom Headers:** the webhook secret from step 2:

\`\`\`json
{"X-Plb-Webhook-Secret": "<the webhook secret>"}
\`\`\`

6. **Payload:** exactly this:

\`\`\`json
{"id": "$ID", "title": "$EVENT_TITLE", "date": "$DATE", "transition": "$ALERT_TRANSITION", "alert_id": "$ALERT_ID", "priority": "$ALERT_PRIORITY", "link": "$LINK", "tags": "$TAGS", "body": "$EVENT_MSG"}
\`\`\`

7. Click **Save**.
8. In each monitor whose alerts an agent should see, add \`@webhook-pliable\` to its message.

A recovery arrives as \`alert.recovered\`; everything else (Triggered, Re-Triggered, Warn, No Data) as \`alert.triggered\`.

---

## 4. Use it in an agent

To start on an alert and read Datadog:

\`\`\`json
"triggers": [{ "connection": "ops-datadog", "event": "alert.triggered", "work_item_key": "alert_id" }],
"capabilities": [{ "connection": "ops-datadog", "capability": "logs.read", "params": {} }]
\`\`\`

\`work_item_key: alert_id\` makes repeats of one monitor join its open piece of work instead of starting another. If your Datadog isn't on \`datadoghq.com\`, set \`"site"\` in the params (for example \`"us5.datadoghq.com"\`).

---

## Troubleshooting

| What you see | What to do |
|---|---|
| Datadog's webhook test gets **401** | The \`X-Plb-Webhook-Secret\` header doesn't match the connection's webhook secret. Replace one to match the other. |
| Datadog's webhook test gets **400** | The payload isn't the one above; it needs at least \`$ID\`. |
| Alerts arrive but no agent runs | Check the agent has a trigger on this connection for \`alert.triggered\`, and that the monitor's message mentions \`@webhook-…\`. |
| A Datadog tool fails with 403 | The application key is missing a scope from step 1, or the person who created it lacks that permission. |
`,
};

export default doc;
